import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { BarChart3, Building2, CheckCircle2, MapPin, RefreshCw, Save, ShieldCheck, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/core/hooks';

interface Branch {
  id: string;
  name: string;
  status: string;
  address: string;
  postcode: string;
  region: string;
  phone: string;
  onCall: string;
  email: string;
  manager: string;
  notes?: string;
}

interface PersonRecord {
  id: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  status?: string;
  branchId?: string;
}

interface BranchReportRow extends Branch {
  metrics: {
    clients: number;
    activeClients: number;
    carers: number;
    activeCarers: number;
    visits: number;
    completedVisits: number;
    missedVisits: number;
    completionRate: number;
    revenue: number;
    payrollCost: number;
    profit: number;
    openInvoices: number;
    documentEvidence: number;
    auditEvents: number;
  };
}

interface BranchReport {
  source: string;
  period: string;
  branches: BranchReportRow[];
  summary: {
    branches: number;
    clients: number;
    carers: number;
    visits: number;
    revenue: number;
    payrollCost: number;
    profit: number;
    unassigned: { clients: number; carers: number; visits: number };
  };
}

const emptyBranch: Branch = {
  id: '',
  name: '',
  status: 'ACTIVE',
  address: '',
  postcode: '',
  region: '',
  phone: '',
  onCall: '',
  email: '',
  manager: '',
  notes: '',
};

function authHeaders(token?: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function loadCollection<T>(collection: string, token?: string | null): Promise<T[]> {
  const response = await fetch(`/api/v1/${collection}`, { headers: authHeaders(token) });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `Could not load ${collection}`);
  return Array.isArray(payload.data) ? payload.data : [];
}

function displayName(record: PersonRecord) {
  return record.fullName || [record.firstName, record.lastName].filter(Boolean).join(' ') || record.email || record.id;
}

function currency(value: number) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(value || 0));
}

export const BranchManagement: React.FC = () => {
  const { token, user } = useAuth();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [clients, setClients] = useState<PersonRecord[]>([]);
  const [carers, setCarers] = useState<PersonRecord[]>([]);
  const [report, setReport] = useState<BranchReport | null>(null);
  const [selectedBranchId, setSelectedBranchId] = useState('branch-1');
  const [draft, setDraft] = useState<Branch>(emptyBranch);
  const [period, setPeriod] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const canEdit = Boolean(user?.roles.some((role) => ['SUPER_ADMIN', 'ADMIN'].includes(role)));
  const selectedBranch = branches.find((branch) => branch.id === selectedBranchId) || branches[0];

  const branchNames = useMemo(
    () => Object.fromEntries(branches.map((branch) => [branch.id, branch.name])),
    [branches]
  );

  const unassignedClients = clients.filter((client) => !client.branchId);
  const unassignedCarers = carers.filter((carer) => !carer.branchId);

  const load = async () => {
    setIsLoading(true);
    try {
      const [nextBranches, nextClients, nextCarers] = await Promise.all([
        loadCollection<Branch>('branches', token),
        loadCollection<PersonRecord>('clients', token),
        loadCollection<PersonRecord>('carers', token),
      ]);
      const reportResponse = await fetch(`/api/branches/report?period=${period}`, {
        headers: authHeaders(token),
      });
      const reportPayload = await reportResponse.json().catch(() => ({}));
      if (!reportResponse.ok) throw new Error(reportPayload?.error || 'Could not load branch report');

      setBranches(nextBranches);
      setClients(nextClients);
      setCarers(nextCarers);
      setReport(reportPayload.data);
      const nextSelected = nextBranches.some((branch) => branch.id === selectedBranchId)
        ? selectedBranchId
        : nextBranches[0]?.id || '';
      setSelectedBranchId(nextSelected);
      setDraft(nextBranches.find((branch) => branch.id === nextSelected) || emptyBranch);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load branch data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [token, period]);

  useEffect(() => {
    setDraft(selectedBranch || emptyBranch);
  }, [selectedBranch?.id]);

  const saveBranch = async () => {
    if (!canEdit) return;
    if (!draft.name.trim()) {
      toast.error('Branch name is required.');
      return;
    }
    setIsSaving(true);
    try {
      const existing = branches.some((branch) => branch.id === draft.id);
      const response = await fetch(`/api/v1/branches${existing ? `/${encodeURIComponent(draft.id)}` : ''}`, {
        method: existing ? 'PATCH' : 'POST',
        headers: {
          ...authHeaders(token),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...draft, id: draft.id || undefined }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not save branch');
      toast.success('Branch saved.');
      await load();
      setSelectedBranchId(payload.data?.id || draft.id || selectedBranchId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save branch');
    } finally {
      setIsSaving(false);
    }
  };

  const assignRecord = async (collection: 'clients' | 'carers', record: PersonRecord, branchId: string) => {
    if (!canEdit) return;
    const response = await fetch(`/api/v1/${collection}/${encodeURIComponent(record.id)}`, {
      method: 'PATCH',
      headers: {
        ...authHeaders(token),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...record, branchId }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      toast.error(payload?.error || 'Could not assign branch');
      return;
    }
    toast.success(`${displayName(record)} assigned to ${branchNames[branchId] || branchId}.`);
    await load();
  };

  const branchReportRows = report?.branches || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-sm font-semibold text-purple-800">
            <Building2 className="h-4 w-4" />
            Multi-branch control
          </div>
          <h1 className="mt-3 text-3xl font-bold text-slate-950">Branches & Locations</h1>
          <p className="mt-2 max-w-3xl text-slate-600">
            Manage operating branches, assign users and records by location, and review separate branch-level performance.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <select
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm"
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
          </select>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-600">
          Loading branch data from the encrypted server database...
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <MetricCard label="Branches" value={report?.summary.branches || branches.length} icon={<Building2 className="h-5 w-5" />} />
            <MetricCard label="Clients" value={report?.summary.clients || clients.length} icon={<Users className="h-5 w-5" />} />
            <MetricCard label="Carers" value={report?.summary.carers || carers.length} icon={<Users className="h-5 w-5" />} />
            <MetricCard label="Branch profit" value={currency(report?.summary.profit || 0)} icon={<BarChart3 className="h-5 w-5" />} />
          </div>

          <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-950">Branch report</h2>
                  <p className="text-sm text-slate-500">Calculated by branch from clients, carers, visits, billing and payroll.</p>
                </div>
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  {report?.source || 'server database'}
                </span>
              </div>
              <div className="mt-5 overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Branch</th>
                      <th className="px-4 py-3">Clients</th>
                      <th className="px-4 py-3">Carers</th>
                      <th className="px-4 py-3">Visits</th>
                      <th className="px-4 py-3">Completion</th>
                      <th className="px-4 py-3">Revenue</th>
                      <th className="px-4 py-3">Payroll</th>
                      <th className="px-4 py-3">Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {branchReportRows.map((branch) => (
                      <tr key={branch.id} className="align-top">
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setSelectedBranchId(branch.id)}
                            className="text-left font-semibold text-purple-800 hover:text-purple-950"
                          >
                            {branch.name}
                          </button>
                          <div className="mt-1 text-xs text-slate-500">{branch.postcode}</div>
                        </td>
                        <td className="px-4 py-3">{branch.metrics.activeClients}/{branch.metrics.clients}</td>
                        <td className="px-4 py-3">{branch.metrics.activeCarers}/{branch.metrics.carers}</td>
                        <td className="px-4 py-3">{branch.metrics.completedVisits}/{branch.metrics.visits}</td>
                        <td className="px-4 py-3">{branch.metrics.completionRate}%</td>
                        <td className="px-4 py-3">{currency(branch.metrics.revenue)}</td>
                        <td className="px-4 py-3">{currency(branch.metrics.payrollCost)}</td>
                        <td className="px-4 py-3 font-semibold text-slate-950">{currency(branch.metrics.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-950">Branch details</h2>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBranchId('');
                      setDraft(emptyBranch);
                    }}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    New branch
                  </button>
                )}
              </div>
              <div className="mt-4 space-y-3">
                <BranchField label="Name" value={draft.name} disabled={!canEdit} onChange={(value) => setDraft({ ...draft, name: value })} />
                <BranchField label="Status" value={draft.status} disabled={!canEdit} onChange={(value) => setDraft({ ...draft, status: value })} />
                <BranchField label="Address" value={draft.address} disabled={!canEdit} onChange={(value) => setDraft({ ...draft, address: value })} />
                <div className="grid gap-3 sm:grid-cols-2">
                  <BranchField label="Postcode" value={draft.postcode} disabled={!canEdit} onChange={(value) => setDraft({ ...draft, postcode: value })} />
                  <BranchField label="Region" value={draft.region} disabled={!canEdit} onChange={(value) => setDraft({ ...draft, region: value })} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <BranchField label="Phone" value={draft.phone} disabled={!canEdit} onChange={(value) => setDraft({ ...draft, phone: value })} />
                  <BranchField label="On-call" value={draft.onCall} disabled={!canEdit} onChange={(value) => setDraft({ ...draft, onCall: value })} />
                </div>
                <BranchField label="Email" value={draft.email} disabled={!canEdit} onChange={(value) => setDraft({ ...draft, email: value })} />
                <BranchField label="Manager" value={draft.manager} disabled={!canEdit} onChange={(value) => setDraft({ ...draft, manager: value })} />
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => void saveBranch()}
                    disabled={isSaving}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-purple-700 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-purple-800 disabled:opacity-60"
                  >
                    <Save className="h-4 w-4" />
                    {isSaving ? 'Saving...' : 'Save branch'}
                  </button>
                )}
              </div>
            </section>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <AssignmentPanel
              title="Clients by branch"
              records={clients}
              branches={branches}
              canEdit={canEdit}
              onAssign={(record, branchId) => void assignRecord('clients', record, branchId)}
            />
            <AssignmentPanel
              title="Carers by branch"
              records={carers}
              branches={branches}
              canEdit={canEdit}
              onAssign={(record, branchId) => void assignRecord('carers', record, branchId)}
            />
          </div>

          <section className="rounded-lg border border-purple-200 bg-purple-50 p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex gap-3">
                <ShieldCheck className="mt-1 h-5 w-5 flex-none text-purple-700" />
                <div>
                  <h2 className="font-bold text-purple-950">Separate permissions per branch</h2>
                  <p className="mt-1 text-sm text-purple-900">
                    Branch-level access is controlled in Roles & Access. Assign branch restrictions to a user or role so they only see that location's clients, carers, visits, invoices and payroll.
                  </p>
                  {(unassignedClients.length > 0 || unassignedCarers.length > 0) && (
                    <p className="mt-2 text-sm font-semibold text-amber-800">
                      Unassigned records: {unassignedClients.length} clients, {unassignedCarers.length} carers.
                    </p>
                  )}
                </div>
              </div>
              <Link
                to="/roles-access"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-bold text-purple-800 shadow-sm ring-1 ring-purple-200 hover:bg-purple-100"
              >
                <ShieldCheck className="h-4 w-4" />
                Open Roles & Access
              </Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

function MetricCard({ label, value, icon }: { label: string; value: React.ReactNode; icon: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="rounded-lg bg-purple-50 p-2 text-purple-700">{icon}</div>
        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
      </div>
      <div className="mt-4 text-2xl font-bold text-slate-950">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

function BranchField({ label, value, disabled, onChange }: { label: string; value: string; disabled: boolean; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <input
        value={value || ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm disabled:bg-slate-50 disabled:text-slate-500"
      />
    </label>
  );
}

function AssignmentPanel({
  title,
  records,
  branches,
  canEdit,
  onAssign,
}: {
  title: string;
  records: PersonRecord[];
  branches: Branch[];
  canEdit: boolean;
  onAssign: (record: PersonRecord, branchId: string) => void;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <MapPin className="h-5 w-5 text-purple-700" />
        <h2 className="text-xl font-bold text-slate-950">{title}</h2>
      </div>
      <div className="mt-4 max-h-[420px] overflow-y-auto rounded-lg border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="sticky top-0 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Branch</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {records.map((record) => (
              <tr key={record.id}>
                <td className="px-4 py-3 font-semibold text-slate-900">{displayName(record)}</td>
                <td className="px-4 py-3 text-slate-500">{record.email || record.phone || '-'}</td>
                <td className="px-4 py-3">
                  <select
                    value={record.branchId || ''}
                    disabled={!canEdit}
                    onChange={(event) => onAssign(record, event.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm disabled:bg-slate-50"
                  >
                    <option value="">Unassigned</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
